// DocumentStatusManager for handling document lifecycle, security, and audit logging
// Provides status tracking, security hooks, and comprehensive audit trail

import { DocumentItem, DocumentStatus, DocumentType } from './types'

// Audit log entry interface
export interface AuditLogEntry {
  id: string
  documentId: string
  timestamp: Date
  action: DocumentAction
  userId?: string
  details: string
  metadata?: Record<string, any>
  ipAddress?: string
  userAgent?: string
}

// Document actions for audit logging
export enum DocumentAction {
  UPLOADED = 'uploaded',
  VALIDATED = 'validated',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  DELETED = 'deleted',
  DOWNLOADED = 'downloaded',
  PREVIEWED = 'previewed',
  SECURITY_SCAN = 'security_scan',
  STATUS_CHANGED = 'status_changed',
  METADATA_UPDATED = 'metadata_updated',
  CATEGORY_CHANGED = 'category_changed'
}

// Status transition rules
export interface StatusTransition {
  from: DocumentStatus
  to: DocumentStatus
  allowedRoles?: string[]
  requiresApproval?: boolean
  autoTransition?: boolean
  conditions?: (document: DocumentItem) => boolean
}

// Security scan result
export interface SecurityScanResult {
  passed: boolean
  threats: string[]
  scanTime: number
  scanProvider: string
  confidence: number
}

// Batch operation result
export interface BatchOperationResult {
  success: number
  failed: number
  errors: { documentId: string; error: string }[]
  auditLogs: AuditLogEntry[]
}

// Document tag interface
export interface DocumentTag {
  id: string
  name: string
  color: string
  category?: string
}

// Status configuration
export const STATUS_TRANSITIONS: StatusTransition[] = [
  {
    from: DocumentStatus.PROCESSING,
    to: DocumentStatus.VALID,
    autoTransition: true,
    conditions: (doc) => !doc.error && doc.validationResult?.isValid === true
  },
  {
    from: DocumentStatus.PROCESSING,
    to: DocumentStatus.REJECTED,
    autoTransition: true,
    conditions: (doc) => !!doc.error || doc.validationResult?.isValid === false
  },
  {
    from: DocumentStatus.VALID,
    to: DocumentStatus.PENDING_REVIEW,
    conditions: (doc) => doc.validationResult?.warnings && doc.validationResult.warnings.length > 0
  },
  {
    from: DocumentStatus.PENDING_REVIEW,
    to: DocumentStatus.VALID,
    allowedRoles: ['admin', 'reviewer']
  },
  {
    from: DocumentStatus.PENDING_REVIEW,
    to: DocumentStatus.REJECTED,
    allowedRoles: ['admin', 'reviewer']
  },
  {
    from: DocumentStatus.VALID,
    to: DocumentStatus.EXPIRED,
    autoTransition: true,
    conditions: (doc) => doc.metadata?.expirationDate ? doc.metadata.expirationDate < new Date() : false
  }
]

export class DocumentStatusManager {
  private static instance: DocumentStatusManager
  private auditLogs: Map<string, AuditLogEntry[]> = new Map()
  private securityHooks: Map<string, (file: File) => Promise<SecurityScanResult>> = new Map()
  private tags: Map<string, DocumentTag[]> = new Map()

  static getInstance(): DocumentStatusManager {
    if (!this.instance) {
      this.instance = new DocumentStatusManager()
    }
    return this.instance
  }

  // Register security scanning provider
  registerSecurityProvider(name: string, scanner: (file: File) => Promise<SecurityScanResult>) {
    this.securityHooks.set(name, scanner)
  }

  // Perform security scan
  async performSecurityScan(file: File, providers: string[] = []): Promise<SecurityScanResult> {
    const scanResults: SecurityScanResult[] = []
    const providersToUse = providers.length > 0 ? providers : Array.from(this.securityHooks.keys())

    if (providersToUse.length === 0) {
      // Mock security scan for demonstration
      return {
        passed: true,
        threats: [],
        scanTime: 100,
        scanProvider: 'mock',
        confidence: 0.95
      }
    }

    for (const providerName of providersToUse) {
      const scanner = this.securityHooks.get(providerName)
      if (scanner) {
        try {
          const result = await scanner(file)
          scanResults.push(result)
        } catch (error) {
          console.error(`Security scan failed for provider ${providerName}:`, error)
          scanResults.push({
            passed: false,
            threats: ['Scan failed'],
            scanTime: 0,
            scanProvider: providerName,
            confidence: 0
          })
        }
      }
    }

    // Combine results - fail if any scanner fails
    return {
      passed: scanResults.every(r => r.passed),
      threats: scanResults.flatMap(r => r.threats),
      scanTime: Math.max(...scanResults.map(r => r.scanTime)),
      scanProvider: scanResults.map(r => r.scanProvider).join(', '),
      confidence: scanResults.length > 0 ? scanResults.reduce((acc, r) => acc + r.confidence, 0) / scanResults.length : 0
    }
  }

  // Validate status transition
  canTransitionTo(document: DocumentItem, newStatus: DocumentStatus, userRole?: string): boolean {
    const transition = STATUS_TRANSITIONS.find(t => t.from === document.status && t.to === newStatus)
    
    if (!transition) {
      return false
    }

    // Check role requirements
    if (transition.allowedRoles && userRole && !transition.allowedRoles.includes(userRole)) {
      return false
    }

    // Check conditions
    if (transition.conditions && !transition.conditions(document)) {
      return false
    }

    return true
  }

  // Transition document status with audit logging
  async transitionStatus(
    document: DocumentItem, 
    newStatus: DocumentStatus,
    userId?: string,
    reason?: string,
    userRole?: string
  ): Promise<DocumentItem> {
    if (!this.canTransitionTo(document, newStatus, userRole)) {
      throw new Error(`Cannot transition from ${document.status} to ${newStatus}`)
    }

    const oldStatus = document.status
    const updatedDocument = { ...document, status: newStatus }

    // Log the status change
    await this.logAction(DocumentAction.STATUS_CHANGED, document.id, userId, 
      `Status changed from ${oldStatus} to ${newStatus}${reason ? `: ${reason}` : ''}`, {
      oldStatus,
      newStatus,
      reason,
      userRole
    })

    return updatedDocument
  }

  // Auto-transition documents based on rules
  async autoTransitionDocuments(documents: DocumentItem[]): Promise<DocumentItem[]> {
    const updatedDocuments: DocumentItem[] = []

    for (const document of documents) {
      let updated = { ...document }
      
      // Find applicable auto-transitions
      for (const transition of STATUS_TRANSITIONS) {
        if (transition.autoTransition && 
            transition.from === document.status && 
            transition.conditions && 
            transition.conditions(document)) {
          
          updated = await this.transitionStatus(document, transition.to, 'system', 'Automatic transition')
          break
        }
      }
      
      updatedDocuments.push(updated)
    }

    return updatedDocuments
  }

  // Log document action
  async logAction(
    action: DocumentAction, 
    documentId: string, 
    userId?: string, 
    details: string = '',
    metadata?: Record<string, any>
  ): Promise<void> {
    const logEntry: AuditLogEntry = {
      id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      documentId,
      timestamp: new Date(),
      action,
      userId,
      details,
      metadata,
      ipAddress: await this.getClientIP(),
      userAgent: navigator.userAgent
    }

    // Store in memory (in production, this would go to a database)
    const existingLogs = this.auditLogs.get(documentId) || []
    existingLogs.push(logEntry)
    this.auditLogs.set(documentId, existingLogs)

    // In production, also send to audit logging service
    // await this.sendToAuditService(logEntry)
  }

  // Get audit trail for document
  getAuditTrail(documentId: string): AuditLogEntry[] {
    return this.auditLogs.get(documentId) || []
  }

  // Get all audit logs (for admin)
  getAllAuditLogs(): AuditLogEntry[] {
    return Array.from(this.auditLogs.values()).flat()
  }

  // Batch status update
  async batchUpdateStatus(
    documents: DocumentItem[], 
    newStatus: DocumentStatus, 
    userId?: string, 
    reason?: string,
    userRole?: string
  ): Promise<BatchOperationResult> {
    const result: BatchOperationResult = {
      success: 0,
      failed: 0,
      errors: [],
      auditLogs: []
    }

    for (const document of documents) {
      try {
        const updated = await this.transitionStatus(document, newStatus, userId, reason, userRole)
        result.success++
        
        // Get the audit log entry for this transition
        const auditLogs = this.getAuditTrail(document.id)
        if (auditLogs.length > 0) {
          result.auditLogs.push(auditLogs[auditLogs.length - 1])
        }
      } catch (error) {
        result.failed++
        result.errors.push({
          documentId: document.id,
          error: error instanceof Error ? error.message : String(error)
        })
      }
    }

    return result
  }

  // Document tagging system
  addTag(documentId: string, tag: DocumentTag): void {
    const existingTags = this.tags.get(documentId) || []
    const tagExists = existingTags.some(t => t.id === tag.id)
    
    if (!tagExists) {
      existingTags.push(tag)
      this.tags.set(documentId, existingTags)
      
      this.logAction(DocumentAction.METADATA_UPDATED, documentId, undefined, 
        `Tag added: ${tag.name}`, { tag })
    }
  }

  removeTag(documentId: string, tagId: string): void {
    const existingTags = this.tags.get(documentId) || []
    const updatedTags = existingTags.filter(t => t.id !== tagId)
    
    if (updatedTags.length !== existingTags.length) {
      this.tags.set(documentId, updatedTags)
      
      this.logAction(DocumentAction.METADATA_UPDATED, documentId, undefined, 
        `Tag removed: ${tagId}`)
    }
  }

  getTags(documentId: string): DocumentTag[] {
    return this.tags.get(documentId) || []
  }

  // Batch operations
  async batchDelete(documentIds: string[], userId?: string): Promise<BatchOperationResult> {
    const result: BatchOperationResult = {
      success: 0,
      failed: 0,
      errors: [],
      auditLogs: []
    }

    for (const documentId of documentIds) {
      try {
        await this.logAction(DocumentAction.DELETED, documentId, userId, 'Document deleted via batch operation')
        result.success++
        
        const auditLogs = this.getAuditTrail(documentId)
        if (auditLogs.length > 0) {
          result.auditLogs.push(auditLogs[auditLogs.length - 1])
        }
      } catch (error) {
        result.failed++
        result.errors.push({
          documentId,
          error: error instanceof Error ? error.message : String(error)
        })
      }
    }

    return result
  }

  // Progress tracking for batch uploads
  createProgressTracker(): {
    updateProgress: (fileIndex: number, progress: number) => void
    getOverallProgress: () => number
    getFileProgress: (fileIndex: number) => number
  } {
    const progressMap = new Map<number, number>()
    
    return {
      updateProgress: (fileIndex: number, progress: number) => {
        progressMap.set(fileIndex, Math.min(100, Math.max(0, progress)))
      },
      getOverallProgress: () => {
        if (progressMap.size === 0) return 0
        const total = Array.from(progressMap.values()).reduce((sum, progress) => sum + progress, 0)
        return total / progressMap.size
      },
      getFileProgress: (fileIndex: number) => {
        return progressMap.get(fileIndex) || 0
      }
    }
  }

  // Get client IP (simplified for demo)
  private async getClientIP(): Promise<string> {
    try {
      // In production, this would use a proper IP detection service
      return 'client-ip-placeholder'
    } catch {
      return 'unknown'
    }
  }

  // Status badge configuration
  static getStatusBadgeConfig(status: DocumentStatus): {
    className: string
    icon: string
    label: string
  } {
    switch (status) {
      case DocumentStatus.VALID:
        return {
          className: 'tw-bg-green-100 tw-text-green-800 tw-border-green-200',
          icon: 'check_circle',
          label: 'Valid'
        }
      case DocumentStatus.EXPIRED:
      case DocumentStatus.REJECTED:
        return {
          className: 'tw-bg-red-100 tw-text-red-800 tw-border-red-200',
          icon: status === DocumentStatus.EXPIRED ? 'schedule' : 'cancel',
          label: status === DocumentStatus.EXPIRED ? 'Expired' : 'Rejected'
        }
      case DocumentStatus.PENDING_REVIEW:
        return {
          className: 'tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-200',
          icon: 'pending',
          label: 'Pending Review'
        }
      case DocumentStatus.PROCESSING:
        return {
          className: 'tw-bg-blue-100 tw-text-blue-800 tw-border-blue-200',
          icon: 'sync',
          label: 'Processing'
        }
      default:
        return {
          className: 'tw-bg-gray-100 tw-text-gray-800 tw-border-gray-200',
          icon: 'help',
          label: 'Unknown'
        }
    }
  }

  // Predefined document tags
  static readonly PREDEFINED_TAGS: DocumentTag[] = [
    { id: 'urgent', name: 'Urgent', color: 'red', category: 'priority' },
    { id: 'verified', name: 'Verified', color: 'green', category: 'status' },
    { id: 'requires-review', name: 'Requires Review', color: 'yellow', category: 'status' },
    { id: 'confidential', name: 'Confidential', color: 'purple', category: 'security' },
    { id: 'archived', name: 'Archived', color: 'gray', category: 'lifecycle' },
    { id: 'original', name: 'Original', color: 'blue', category: 'type' },
    { id: 'copy', name: 'Copy', color: 'orange', category: 'type' }
  ]
}

export default DocumentStatusManager